import { Check, Sparkles, UploadCloud } from "lucide-react"
import { useId, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { AnimatedSourcePlaceholder } from "@/components/app/animated-source-placeholder"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import { authClient } from "@/lib/auth-client"
import { isSupportedVideoUrl } from "@/lib/video-source"

export function LandingSourceForm({
  showTrustLine = true,
}: {
  showTrustLine?: boolean
}) {
  const inputId = useId()
  const errorId = `${inputId}-error`
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const [videoUrl, setVideoUrl] = useState("")
  const [sourceError, setSourceError] = useState<string | null>(null)

  function continueToWorkspace(source?: string) {
    const search = source ? { source } : {}
    return navigate({
      to: session ? "/app" : "/signup",
      search,
    })
  }

  function continueWithVideo(value: string) {
    const source = value.trim()
    if (!isSupportedVideoUrl(source)) {
      setSourceError(
        "Paste a valid HTTPS YouTube video or public Twitch VOD link."
      )
      return
    }
    setSourceError(null)
    void continueToWorkspace(source)
  }

  return (
    <>
      <form
        className="forge-source-form"
        onSubmit={(event) => {
          event.preventDefault()
          continueWithVideo(videoUrl)
        }}
      >
        <label htmlFor={inputId} className="sr-only">
          YouTube or public Twitch VOD link
        </label>
        <InputGroup
          className="forge-source-input"
          data-invalid={Boolean(sourceError)}
        >
          {!videoUrl && <AnimatedSourcePlaceholder />}
          <InputGroupInput
            id={inputId}
            type="url"
            placeholder=""
            value={videoUrl}
            aria-invalid={Boolean(sourceError)}
            aria-describedby={sourceError ? errorId : undefined}
            onChange={(event) => {
              setVideoUrl(event.target.value)
              setSourceError(null)
            }}
            onPaste={(event) => {
              const source = event.clipboardData.getData("text").trim()
              if (!isSupportedVideoUrl(source)) return
              event.preventDefault()
              setVideoUrl(source)
              continueWithVideo(source)
            }}
          />
          <InputGroupAddon align="inline-start">
            <InputGroupButton
              size="icon-sm"
              aria-label="Upload a video in the app"
              title="Upload a video in the app"
              onClick={() => void continueToWorkspace()}
            >
              <UploadCloud aria-hidden="true" />
            </InputGroupButton>
          </InputGroupAddon>
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="submit"
              variant="default"
              size="sm"
              disabled={!videoUrl.trim()}
            >
              <Sparkles data-icon="inline-start" aria-hidden="true" />
              Generate
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        {sourceError && (
          <p id={errorId} className="forge-source-error" role="alert">
            {sourceError}
          </p>
        )}
      </form>
      {showTrustLine && (
        <p className="forge-trust-line">
          <Check aria-hidden="true" /> Start with a free trial. No credit card
          required.
        </p>
      )}
    </>
  )
}
